import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, ShieldCheck, UserPlus } from 'lucide-react'

import { api } from '@/api/client'
import { useAuth, type Company } from '@/stores/auth'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { cn } from '@/lib/utils'

type Role = 'admin' | 'member'

interface Membership { company: number; company_name?: string; role: Role }

interface UserAdmin {
  id: number
  username: string
  display_name: string
  is_active: boolean
  is_master: boolean
  can_analyze: boolean
  memberships: Membership[]
}

interface UserForm {
  id?: number
  username: string
  display_name: string
  password: string
  is_active: boolean
  can_analyze: boolean
  memberships: Membership[]
}

const blankForm: UserForm = {
  username: '', display_name: '', password: '', is_active: true, can_analyze: false, memberships: [],
}

const ROLE_LABEL: Record<Role, string> = { admin: '관리자', member: '일반' }

export function UsersPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const me = useAuth((s) => s.user)

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<UserForm>(blankForm)

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api<UserAdmin[]>('/api/users'),
  })
  const { data: companies = [] } = useQuery({
    queryKey: ['companies', ''],
    queryFn: () => api<Company[]>('/api/companies?q='),
  })

  const save = useMutation({
    mutationFn: (f: UserForm) => {
      const body: Record<string, unknown> = {
        username: f.username, display_name: f.display_name,
        is_active: f.is_active, can_analyze: f.can_analyze, memberships: f.memberships.map((m) => ({ company: m.company, role: m.role })),
      }
      if (f.password) body.password = f.password
      return editId == null
        ? api('/api/users', { method: 'POST', body })
        : api(`/api/users/${editId}`, { method: 'PATCH', body })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      push(editId == null ? '사용자 추가됨' : '사용자 저장됨', 'success')
      setOpen(false); setForm(blankForm); setEditId(null)
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error'),
  })

  const deactivate = useMutation({
    mutationFn: (id: number) => api(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); push('비활성화됨', 'success') },
    onError: (e) => push(`실패: ${e.message}`, 'error'),
  })

  function openAdd() {
    setEditId(null); setForm(blankForm); setOpen(true)
  }
  function openEdit(u: UserAdmin) {
    setEditId(u.id)
    setForm({
      id: u.id, username: u.username, display_name: u.display_name || '',
      password: '', is_active: u.is_active, can_analyze: u.can_analyze,
      memberships: u.memberships.map((m) => ({ company: m.company, role: m.role })),
    })
    setOpen(true)
  }

  function toggleCompany(cid: number, on: boolean) {
    setForm((f) => on
      ? { ...f, memberships: [...f.memberships, { company: cid, role: 'member' }] }
      : { ...f, memberships: f.memberships.filter((m) => m.company !== cid) })
  }
  function setRole(cid: number, role: Role) {
    setForm((f) => ({ ...f, memberships: f.memberships.map((m) => m.company === cid ? { ...m, role } : m) }))
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-md">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">사용자 관리</h1>
            <p className="text-sm text-slate-300 mt-0.5">서브 계정을 만들고 접근 가능한 회사를 배정합니다</p>
          </div>
          <button onClick={() => nav({ to: '/company-select' })}
                  className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> 회사관리
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex justify-end mb-4">
          <button onClick={openAdd}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-1.5">
            <UserPlus className="w-4 h-4" /> 사용자 추가
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">아이디</th>
                <th className="text-left px-4 py-2.5 font-medium">이름</th>
                <th className="text-left px-4 py-2.5 font-medium">접근 회사</th>
                <th className="text-center px-4 py-2.5 font-medium w-20">상태</th>
                <th className="px-4 py-2.5 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {u.username}
                    {u.is_master && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full align-middle">
                        <ShieldCheck className="w-3 h-3" /> 마스터
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{u.display_name || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">
                    {u.is_master
                      ? <span className="text-amber-700">전체 회사</span>
                      : (u.memberships.length
                          ? u.memberships.map((m) => `${m.company_name}(${ROLE_LABEL[m.role]})`).join(', ')
                          : <span className="text-slate-400">배정 없음</span>)}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-semibold',
                      u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500')}>
                      {u.is_active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {u.is_master || u.id === me?.id ? (
                      <span className="text-xs text-slate-300">—</span>
                    ) : (
                      <>
                        <button onClick={() => openEdit(u)} className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                        {u.is_active && (
                          <button onClick={() => deactivate.mutate(u.id)} className="text-rose-500 hover:text-rose-700 text-xs">비활성화</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-10 text-sm">사용자가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          마스터 계정은 이 화면에서 수정할 수 없습니다 (Django 관리자에서 관리). 마스터는 모든 회사에 접근합니다.
        </p>
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null) }}
             title={editId == null ? '사용자 추가' : '사용자 수정'} size="2xl">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form) }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">아이디 *</label>
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                     required disabled={editId != null}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">이름</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              비밀번호 {editId != null && <span className="font-normal text-slate-400">(변경 시에만 입력)</span>}
            </label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   autoComplete="new-password" placeholder={editId != null ? '비워두면 기존 유지' : ''}
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500"/>
            {/* 서버 규칙(settings.AUTH_PASSWORD_VALIDATORS)과 같은 안내 */}
            <p className="text-[11px] text-slate-400 mt-1">
              8자 이상 · 영문과 숫자 함께 · 아이디·이름과 비슷하거나 흔한 비밀번호(qwer1234 등) 불가
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">접근 가능한 회사</label>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
              {companies.map((c) => {
                const m = form.memberships.find((x) => x.company === c.id)
                return (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={!!m} onChange={(e) => toggleCompany(c.id, e.target.checked)}
                           className="accent-emerald-600 w-4 h-4"/>
                    <span className="flex-1 text-slate-700">{c.name}</span>
                    {m && (
                      <select value={m.role} onChange={(e) => setRole(c.id, e.target.value as Role)}
                              className="text-xs px-2 py-1 rounded border border-slate-300 bg-white">
                        <option value="member">일반</option>
                        <option value="admin">관리자</option>
                      </select>
                    )}
                  </label>
                )
              })}
              {!companies.length && <div className="px-3 py-4 text-xs text-slate-400 text-center">회사가 없습니다.</div>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-1">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                   className="accent-emerald-600 w-4 h-4"/>
            계정 활성화
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.can_analyze} onChange={(e) => setForm({ ...form, can_analyze: e.target.checked })}
                   className="accent-emerald-600 w-4 h-4"/>
            분석 화면 보기 <span className="text-xs text-slate-400">(회사관리 '분석' 탭 — 관리자는 항상 가능)</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => { setOpen(false); setEditId(null) }}
                    className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
            <button type="submit" disabled={!form.username || save.isPending}
                    className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-semibold rounded-lg inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> {editId == null ? '추가' : '저장'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
